import type { ApiErrorBody } from './api';

/**
 * Stale-write protection (T-150 audit, decision 44 a). A save of a record several people edit
 * sends `expected_updated_at`, the version it started from. When the record has changed since,
 * the Worker saves nothing and answers 409 `<DOMAIN>_STALE` with who changed it and when; the page
 * keeps the person's draft and shows DataChangedBanner with Reload.
 */

export const BRAND_STALE = 'BRAND_STALE' as const;
export const SUPPLIER_STALE = 'SUPPLIER_STALE' as const;
export const TASK_DESCRIPTION_STALE = 'TASK_DESCRIPTION_STALE' as const;
/** An import shipment's header or Plan (Imports). Shipments don't record who saved: `updated_by` is null. */
export const SHIPMENT_STALE = 'SHIPMENT_STALE' as const;
/** An import price list's details or "Make current" (Imports). Price lists don't record who saved: `updated_by` is null. */
export const PRICE_LIST_STALE = 'PRICE_LIST_STALE' as const;
/** A price list line edited by hand (Imports), with who saved it last. */
export const PRICE_LIST_LINE_STALE = 'PRICE_LIST_LINE_STALE' as const;
export const SWATCH_STALE = 'SWATCH_STALE' as const;
/** A product type noun's set-up (T-329): someone saved it, or the AI or an Apply changed it, since you opened it. */
export const NOUN_STALE = 'NOUN_STALE' as const;
/** A search term's categories (T-67): someone corrected it, or dropped the correction, since you opened it. */
export const SEARCH_TERM_STALE = 'SEARCH_TERM_STALE' as const;

/** A customer notification's draft (T-305): someone saved the draft, or marked it as pasted, since you opened it. */
export const NOTIFICATION_DRAFT_STALE = 'NOTIFICATION_DRAFT_STALE' as const;

/** Customer notifications' email settings (T-281): someone saved them since you opened them. */
export const NOTIFICATION_SETTINGS_STALE = 'NOTIFICATION_SETTINGS_STALE' as const;

/** A newsletter (T-382): someone else's autosave landed since you opened it. NEWSLETTER_SETTINGS_STALE for its settings. */
export { NEWSLETTER_STALE, NEWSLETTER_SETTINGS_STALE } from '../newsletter/api';

/** Who saved the newer version. `name` is null when it was a sync or the person has gone. */
export interface StaleWriteActor {
  user_id: string | null;
  name: string | null;
}

/** The 409 body of a stale save. */
export interface StaleWriteBody extends ApiErrorBody {
  updated_by: StaleWriteActor | null;
  /** When the newer version was saved (ISO). */
  updated_at: string | null;
}

/** True for any `<DOMAIN>_STALE` code (product batches' `PRODUCT_BATCH_PRODUCT_STALE` included). */
export function isStaleWriteCode(code: string | null | undefined): boolean {
  return typeof code === 'string' && code.endsWith('_STALE');
}
